<script>
import moment from 'moment'
import { sumBy } from 'lodash'
import { Errors } from 'form-backend-validation'

import product from './product.vue'
import currencyInput from './currency-input.vue'
import quotationControl from './quotation-control.vue'
import { mapActions, mapState } from 'vuex'
import SaleContractControl from './sale-contract-control.vue'
import { debounce } from 'lodash'
import AddingSerial from './adding-serial.vue'

export default {
  components: {
    currencyInput,
    product,
    quotationControl,
    SaleContractControl,
    AddingSerial
  },
  name: 'orderItem',
  props: ['value', 'validate'],
  data() {
    return {
      searchProduct: '',
      errors: new Errors(),
      addedProducts: [],
      model: {},
      showProductModal: false,
      showProductSerialDrawer: false,
      addingSerialProduct: {},
      searchSerial: ''
    }
  },
  mounted() {
    this.model = this.value
  },
  computed: {
    ...mapState('saleInvoiceStore', ['currencies']),
    countedDetail() {
      if (this.model.product_detail === undefined) return false

      return this.model.product_detail.length
    },
    uomListByProduct: vm => productId => {
      if (productId === undefined) return []

      const product = vm.addedProducts.find(p => p.product_id === productId)

      if (product !== undefined) {
        return product.uom_category.unit_of_measures.map(el => ({
          value: el.uom_id,
          label: el.uom_name_en
        }))
      }

      return []
    },
    currencySignById: vm => currencyId => {
      if (vm.currencies) {
        const currency = vm.currencies.find(c => c.currency_id === currencyId)
        return currency ? currency.currency_symbol : ''
      }

      return '-'
    },
    defaultCurrencyId() {
      if (this.currencies) {
        const currency = this.currencies.find(c => c.is_default)
        return currency ? currency.currency_id : undefined
      }

      return undefined
    },
    defaultCurrencySign() {
      if (this.currencies) {
        const currency = this.currencies.find(c => c.is_default)
        return currency ? currency.currency_symbol : undefined
      }
      return undefined
    }
  },
  methods: {
    ...mapActions('saleInvoiceStore', ['onFindSerial', 'onGetProduct']),
    showProduct() {
      this.showProductModal = true
      this.$nextTick(() => {
        this.$refs.searchProduct.focus()
        const product = this.$refs.product.products
        if (!product.length) {
          this.$refs.product.fetchProduct()
        }
      })
    },
    onCloseProductDrawer() {
      this.showProductModal = false
    },
    onReceiveSelect(product) {
      this.pushAddedProduct(product)
      const data = this.setValueCurrency(this.serializeData(product))
      this.model.product_detail.push(data)
      this.updateMainAmount()
    },
    pushAddedProduct(product) {
      const existing = this.addedProducts.find(
        p => p.product_id === product.product_id
      )
      if (!existing) {
        this.addedProducts.push(product)
      }
    },
    serializeData(product) {
      const uom_id = this.findDefaultUom(product)
      const unitPrice = this.getSalePrice(product, uom_id)

      // Calculate subtotal before discount
      const subtotal = (product.quantity ? product.quantity : 1) * unitPrice

      return {
        product_id: product.product_id,
        product_name_en: product.product_name_en,
        product_name_kh: product.product_name_kh,
        barcode: product.barcode,
        uom_id: uom_id,
        quantity: product.quantity ? product.quantity : 1,

        unit_price: unitPrice,
        input_unit_price: this.getSalePrice(product, uom_id), // add new

        discount_type: '%',
        discount_rate: 0,

        input_discount_amount: 0, // add new
        discount_amount: 0,

        total_after_discount: 0,
        vat_rate: 0,
        vat_amount: 0,

        grand_total: subtotal,

        promo_ref_promo_product_id: null,
        promo_type_id: '',
        vat_eligible: product.vat_eligible,
        serial_list: [],
        is_custom: false,
        is_calculate_stock: product.is_calculate_stock,
        has_serial: product.is_generate_unique_serial,
        ending_balance: product.ending_balance,
        cost: parseFloat(this.getCost(product, uom_id)),

        fx_rate: undefined,
        fx_rate_ratio: undefined,
        input_currency_id: undefined,
        currency_id: undefined
      }
    },
    findDefaultUom(product) {
      const uom = product.uom_category.unit_of_measures.find(u => u.is_default)
      if (uom) {
        return uom.uom_id
      }
      return undefined
    },
    getCost(product, uomId) {
      if (product.sale_code) {
        const saleCode = product.sale_code.find(s => s.uom_id === uomId)
        if (saleCode !== undefined) {
          return saleCode.product_price.cost
        }
      }
      return 0
    },
    getSalePrice(product, uomId) {
      if (product.sale_code) {
        const saleCode = product.sale_code.find(s => s.uom_id === uomId)
        if (saleCode !== undefined) {
          return saleCode.product_price.sale_price
        }
      }
      return 0
    },
    setValueCurrency(data) {
      const findDefaultCurrency = this.currencies.find(c => c.is_default)
      if (findDefaultCurrency) {
        data.currency_id = findDefaultCurrency.currency_id
        data.input_currency_id = findDefaultCurrency.currency_id
        data.fx_rate = 1
        data.fx_rate_ratio = 1
      }

      return data
    },
    updateCurrency(productRow, currencyId) {
      productRow.input_currency_id = currencyId

      if (currencyId === this.defaultCurrencyId) {
        productRow.fx_rate = 1
        productRow.fx_rate_ratio = 1
        this.onCalculateWhenChange(productRow)
        return
      }

      // find exchange from currency to default
      const exchangeRate = this.currencies.find(
        c => c.currency_id === productRow.input_currency_id
      )

      if (exchangeRate.last_exchange_rate !== undefined) {
        const { fx_rate, fx_rate_ratio } = exchangeRate.last_exchange_rate
        // update fx rate and ratio
        productRow.fx_rate = fx_rate
        productRow.fx_rate_ratio = fx_rate_ratio
      }

      this.onCalculateWhenChange(productRow)
    },
    removeItem(index) {
      this.model.product_detail.splice(index, 1)
      this.updateMainAmount()
    },
    onCalculateWhenChange(row) {
      // Calculate subtotal before discount
      const subtotal = row.quantity * row.input_unit_price

      // convert from input_unit_price to default currency value
      this.calculateAmountToDefaultCurrency(row, 'unit_price')

      // Calculate discount amount
      if (row.discount_type === '%') {
        row.input_discount_amount = (subtotal * row.discount_rate) / 100
        this.calculateAmountToDefaultCurrency(row, 'discount_amount')
      } else {
        row.input_discount_amount = row.discount_rate
        // calcualte discount amount from input_discount_amount to default currency
        this.calculateAmountToDefaultCurrency(row, 'discount_amount')
      }

      row.total_after_discount =
        row.quantity * row.unit_price - row.discount_amount

      // Calculate VAT amount if product is VAT eligible
      if (row.vat_eligible) {
        row.vat_amount = (row.total_after_discount * row.vat_rate) / 100
      } else {
        row.vat_amount = 0
      }
      row.grand_total = row.total_after_discount + row.vat_amount

      // update sub total on main
      this.updateMainAmount()
    },

    // get price base on uom
    onUomChange(row) {
      const product = this.addedProducts.find(
        p => p.product_id === row.product_id
      )
      const unitPrice = this.getSalePrice(product, row.uom_id)

      row.unit_price = unitPrice
      row.input_unit_price = unitPrice

      this.onCalculateWhenChange(row)
    },
    calculateAmountToDefaultCurrency(row, keyColunm) {
      if (row.input_currency_id === this.defaultCurrencyId)
        return (row[keyColunm] = row[`input_${keyColunm}`])

      row[keyColunm] = row[`input_${keyColunm}`] * row.fx_rate_ratio
    },
    updateMainAmount() {
      // update sub total amount to main
      this.model.sub_total = sumBy(this.model.product_detail, 'grand_total')

      this.model.total_after_discount =
        this.model.sub_total -
        this.model.input_discount * this.model.fx_rate_ratio

      if (this.model.vat_rate > 0) {
        this.vat_amount =
          (this.model.total_after_discount * this.model.vat_rate) / 100
      }

      this.model.grand_total =
        this.model.total_after_discount + this.model.vat_amount

      this.model.amount_due = this.model.grand_total - this.model.amount_paid

      // next payment date if amount due is 0
      if (this.model.amount_due === 0) {
        this.model.next_payment_date = null
      }
      if (this.model.amount_due > 0) {
        this.model.next_payment_date = moment(
          this.model.stock_out_date,
          'DD-MM-YYYY'
        )
          .add(this.model.credit_day, 'days')
          .format('DD-MM-YYYY')
      }
    },
    addProductFromReference(products) {
      this.addedProducts = []
      this.addedProducts.push(...products)
    },
    onSearchProduct: debounce(function() {
      this.$refs.product.params.search = this.searchProduct
      this.$refs.product.fetchProduct()
    }, 800),
    showAddSerial(product) {
      this.showProductSerialDrawer = true
      this.addingSerialProduct = product
    },
    onCloseDrawerSerial() {
      this.showProductSerialDrawer = false
      this.addingSerialProduct = {}
    },
    async onSearchSerial(value) {
      if (!value) return

      this.$store.commit('saleInvoiceStore/SET_LOADING', true)
      const serial = await this.onFindSerial(value)
        .catch(error => {
          this.$notification.error({
            placement: 'bottomRight',
            message: 'Errors',
            description: error.response.data.message,
            style: { 'z-index': 1021, color: 'red' }
          })
        })
        .finally(() => {
          this.searchSerial = ''
        })

      await this.addSerialToList(serial)

      this.$store.commit('saleInvoiceStore/SET_LOADING', false)
      // searchSerial
    },
    async addSerialToList(serial) {
      if (serial === undefined) {
        this.$store.commit('saleInvoiceStore/SET_LOADING', false)
        return
      }

      const findExisting = this.model.product_detail.find(
        d => d.product_id === serial.data.product_id
      )

      if (findExisting === undefined) {
        const product = await this.onGetProduct({
          params: {
            product_id: serial.data.product_id
          }
        })

        if (product.data.length === 1) {
          this.pushAddedProduct(product.data[0])
          const data = this.setValueCurrency(
            this.serializeData(product.data[0])
          )
          data.serial_list.push({
            serial_no: serial.data.serial_no,
            product_grade: serial.data.product_grade,
            cost: serial.data.cost
          })
          this.onCalculateWhenChange(data)
          this.model.product_detail.push(data)
        }
      } else {
        const findSerial = findExisting.serial_list.find(
          s => s.serial_no === serial.data.serial_no
        )
        if (!findSerial) {
          findExisting.serial_list.push({
            serial_no: serial.data.serial_no,
            product_grade: serial.data.product_grade,
            cost: serial.data.cost
          })

          findExisting.quantity = findExisting.serial_list.length

          this.onCalculateWhenChange(findExisting)
        } else {
          this.$notification.error({
            placement: 'bottomRight',
            message: 'Notified',
            description:
              'Serial Number: ' + serial.data.serial_no + ' already added.',
            style: { 'z-index': 1021, color: '#135e5e' }
          })
        }
      }
      this.updateMainAmount()
    }
  },
  watch: {
    validate: function(error) {
      this.errors = new Errors(error.errors)
    }
  }
}
</script>

<template>
  <div>
    <div class="ts-flex ts-justify-between">
      <h3 class="ts-text-gray-500 ts-mb-0">
        <i class="fas fa-shopping-basket"></i>
        {{ $t('saleInvoice.ordered') }}
      </h3>
      <div class="ts-space-x-2 ts-flex">
        <a-input-search
          v-model="searchSerial"
          ref="searchSerial"
          size="small"
          :placeholder="$t('saleInvoice.searchSerialOnly')"
          @search="onSearchSerial"
        ></a-input-search>
        <quotationControl @product="addProductFromReference" v-model="model" />
        <sale-contract-control
          @product="addProductFromReference"
          v-model="model"
        />
        <ts-button @click.prevent="showProduct">
          <i class="fas fa-shopping-cart"></i>
        </ts-button>
      </div>
    </div>
    <!-- header table -->
    <div class="grid-product ts-mt-3">
      <div
        class="ts-text-center ts-uppercase ts-text-sm ts-font-semibold ts-bg-gray-200 ts-text-gray-700 ts-py-2 ts-px-2 ts-whitespace-no-wrap"
      >
        {{ $t('saleInvoice.numeric') }}
      </div>
      <div
        class="ts-uppercase ts-text-sm ts-font-semibold ts-bg-gray-200 ts-text-gray-700 ts-py-2 ts-px-2"
      >
        {{ $t('saleInvoice.product') }}
      </div>
      <div
        class="ts-uppercase ts-text-sm ts-font-semibold ts-bg-gray-200 ts-text-gray-700 ts-py-2 ts-px-2 ts-text-center"
      >
        {{ $t('saleInvoice.quantity') }}
        <span class="ts-text-red-500">*</span>
      </div>
      <div
        class="ts-uppercase ts-text-sm ts-font-semibold ts-bg-gray-200 ts-text-gray-700 ts-py-2 ts-px-2"
      >
        {{ $t('saleInvoice.uom') }}
        <span class="ts-text-red-500">*</span>
      </div>
      <div
        class="ts-uppercase ts-text-sm ts-font-semibold ts-bg-gray-200 ts-text-gray-700 ts-py-2 ts-px-2"
      >
        {{ $t('saleInvoice.unitPrice') }}
        <span class="ts-text-red-500">*</span>
      </div>
      <div
        class="ts-uppercase ts-text-sm ts-font-semibold ts-bg-gray-200 ts-text-gray-700 ts-py-2 ts-px-2"
      >
        {{ $t('saleInvoice.discountRate') }}
      </div>
      <div
        class=" ts-text-center ts-uppercase ts-text-sm ts-font-semibold ts-bg-gray-200 ts-text-gray-700 ts-py-2 ts-px-2 ts-whitespace-no-wrap"
      >
        {{ $t('saleInvoice.vatRate') }}
      </div>
      <div
        class="ts-uppercase ts-text-sm ts-font-semibold ts-bg-gray-200 ts-text-gray-700 ts-py-2 ts-px-2"
      >
        {{ $t('saleInvoice.grandTotal') }}
      </div>
      <div
        class="ts-uppercase ts-text-sm ts-font-semibold ts-bg-gray-200 ts-text-gray-700 ts-py-2 ts-px-2"
      >
        {{ $t('saleInvoice.amountIn') + ' (' + defaultCurrencySign + ')' }}
      </div>
      <div
        class="ts-uppercase ts-text-sm ts-text-center ts-font-semibold ts-bg-gray-200 ts-text-gray-700 ts-py-2 ts-px-2"
      >
        {{ $t('actions') }}
      </div>
    </div>

    <!-- product -->
    <div style="max-height: 350px; overflow-y: scroll;">
      <div
        v-for="(product, i) in model.product_detail"
        class="grid-product"
        :key="i"
      >
        <div
          class="ts-text-center ts-py-2 ts-flex ts-items-center ts-pl-4 ts-border-t"
          :class="{ 'ts-bg-gray-100': i % 2 !== 0 }"
        >
          {{ i + 1 }}
        </div>
        <div
          class=" ts-py-2 ts-flex ts-items-center ts-px-2 ts-border-t"
          :class="{ 'ts-bg-gray-100': i % 2 !== 0 }"
        >
          <div>
            {{ product.product_name_en }}
            <p
              v-if="errors.has('product_detail.' + i + '.serial_list')"
              class="ts-text-red-500"
            >
              {{ errors.first('product_detail.' + i + '.serial_list') }}
            </p>
          </div>
        </div>
        <div
          class=" ts-py-2 ts-flex ts-items-center ts-px-2 ts-border-t ts-text-center"
          :class="{ 'ts-bg-gray-100': i % 2 !== 0 }"
        >
          <input
            v-model="product.quantity"
            :readonly="product.has_serial"
            placeholder="Input Qty"
            type="number"
            class="ts-w-full ts-text-center ts-outline-none"
            style="padding: 5px 0;"
            :class="{
              'ts-border ts-rounded ts-border-red-500': errors.has(
                'product_detail.' + i + '.quantity'
              )
            }"
            @input="onCalculateWhenChange(product)"
          />
        </div>
        <div
          class=" ts-py-2 ts-flex ts-items-center ts-px-3 ts-border-t"
          :class="{ 'ts-bg-gray-100': i % 2 !== 0 }"
        >
          <select
            v-model="product.uom_id"
            class="ts-w-full ts-outline-none ts-cursor-pointer"
            style="padding: 5px 0;"
            :class="{
              'ts-border ts-rounded ts-border-red-500': errors.has(
                'product_detail.' + i + '.uom_id'
              )
            }"
            @change="onUomChange(product)"
          >
            <option value="">--select--</option>
            <option
              v-for="(u, uIndex) in uomListByProduct(product.product_id)"
              :key="uIndex"
              :value="u.value"
              >{{ u.label }}</option
            >
          </select>
        </div>
        <div
          class=" ts-py-2 ts-border-t ts-flex ts-items-center"
          :class="{
            'ts-bg-gray-100': i % 2 !== 0,
            'ts-border ts-rounded ts-border-red-500': errors.has(
              'product_detail.' + i + '.uom_id'
            )
          }"
        >
          <currency-input
            :currencyId="product.input_currency_id"
            v-model.number="product.input_unit_price"
            @currency-change="currencyId => updateCurrency(product, currencyId)"
            @input="onCalculateWhenChange(product)"
            :class="{
              'ts-border ts-rounded ts-border-red-500 ts-py-1': errors.has(
                'product_detail.' + i + '.input_unit_price'
              )
            }"
          />
        </div>
        <div
          class=" ts-py-2 ts-items-center ts-px-3 ts-border-t ts-flex"
          :class="{ 'ts-bg-gray-100': i % 2 !== 0 }"
        >
          <select
            v-model="product.discount_type"
            class="ts-text-center ts-outline-none"
            @change="onCalculateWhenChange(product)"
          >
            <option value="%">%</option>
            <option :value="currencySignById(product.input_currency_id)">
              {{ currencySignById(product.input_currency_id) }}
            </option>
          </select>
          <input
            v-model="product.discount_rate"
            type="number"
            class="ts-w-full ts-text-center ts-outline-none"
            @input="onCalculateWhenChange(product)"
          />
        </div>
        <div
          class=" ts-py-2 ts-flex ts-items-center ts-px-3 ts-border-t"
          :class="{ 'ts-bg-gray-100': i % 2 !== 0 }"
        >
          <input
            v-model="product.vat_rate"
            type="number"
            class="ts-w-full ts-text-center ts-outline-none"
            :readonly="!product.vat_eligible"
            @input="onCalculateWhenChange(product)"
          />
        </div>
        <div
          class=" ts-py-2 ts-flex ts-items-center ts-px-3 ts-border-t"
          :class="{ 'ts-bg-gray-100': i % 2 !== 0 }"
        >
          <currency-input
            :value="product.grand_total / product.fx_rate_ratio"
            :currencyId="product.input_currency_id"
            :isDefault="true"
            class="ts-text-green-500 ts-font-robot"
          />
        </div>
        <div
          class=" ts-py-2 ts-flex ts-items-center ts-px-3 ts-border-t"
          :class="{ 'ts-bg-gray-100': i % 2 !== 0 }"
        >
          <currency-input
            :value="product.grand_total"
            :currencyId="defaultCurrencyId"
            :isDefault="true"
            class="ts-text-blue-500 ts-font-robot ts-font-semibold"
          />
        </div>
        <div
          class=" ts-py-2 ts-flex ts-items-center ts-space-x-3 ts-px-2 ts-border-t ts-justify-center"
          :class="{ 'ts-bg-gray-100': i % 2 !== 0 }"
        >
          <a
            href="#"
            class="ts-text-blue-600"
            v-if="product.has_serial"
            @click.prevent="showAddSerial(product)"
          >
            <i class="far fa-list-alt"></i>
          </a>
          <a href="#" class="ts-text-red-600" @click.prevent="removeItem(i)">
            <i class="fa fa-trash"></i>
          </a>
        </div>

        <!-- footer -->
      </div>
    </div>
    <div
      v-if="!countedDetail"
      class="ts-p-2 ts-h-48 ts-flex ts-items-center ts-justify-center"
    >
      <div class="ts-text-center">
        <p>No items added yet. Click "Add Item" to start.</p>
        <ts-button color="primary" @click.prevent="showProduct">
          <i class="fas fa-plus"></i>
          {{ $t('saleInvoice.addItem') }}
        </ts-button>
      </div>
    </div>

    <a-drawer
      :visible="showProductModal"
      :title="$t('saleInvoice.product')"
      :footer="null"
      :width="820"
      :zIndex="1024"
      :maskClosable="true"
      @close="onCloseProductDrawer"
    >
      <a-input-search
        v-model="searchProduct"
        ref="searchProduct"
        class="ts-w-full"
        :placeholder="$t('saleInvoice.searchProduct')"
        @search="onSearchProduct"
      >
      </a-input-search>
      <product v-model="value" ref="product" @selectedProduct="onReceiveSelect" />
    </a-drawer>

    <a-drawer
      :visible="showProductSerialDrawer"
      :title="$t('saleInvoice.addSerial')"
      :footer="null"
      :width="300"
      :zIndex="1024"
      :maskClosable="true"
      @close="onCloseDrawerSerial"
    >
      <AddingSerial
        :product="addingSerialProduct"
        @calculate="() => onCalculateWhenChange(addingSerialProduct)"
      />
    </a-drawer>
  </div>
</template>

<style scoped>
/* Chrome, Safari, Edge, Opera 	5220017520	*/
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type='number'] {
  -moz-appearance: textfield;
  background: transparent;
}

.grid-product {
  display: grid;
  grid-template-columns: 50px auto 120px 100px 120px 100px 100px 150px 150px 100px;
}

select {
  appearance: none;
  background: transparent;
}
</style>
